import { useState, useEffect } from 'react';
import { Usd, PriceCaption } from '@/utils/CurrencyContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ShoppingCart, Search, Book, Wrench, Package, Download, ArrowLeft, Plus, Filter } from 'lucide-react';
import { motion } from 'framer-motion';
import { isAuthenticated } from '@/utils/auth';

const CATEGORY_ICONS = {
  books: Book,
  equipment: Wrench,
  kits: Package,
  digital: Download,
};

const MarketplacePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    if (isAuthenticated()) {
      fetchCartCount();
    }
  }, [selectedCategory, searchParams]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/marketplace/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await api.get(`/marketplace/products?${params.toString()}`);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCartCount = async () => {
    try {
      const response = await api.get('/marketplace/cart');
      setCartCount(response.data.item_count);
    } catch (error) {
      // Ignore if not logged in
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchQuery) {
      newParams.set('search', searchQuery);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
    fetchProducts();
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    const newParams = new URLSearchParams(searchParams);
    if (category) {
      newParams.set('category', category);
    } else {
      newParams.delete('category');
    }
    setSearchParams(newParams);
  };

  const addToCart = async (productId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated()) {
      toast.error('Please log in to add items to cart');
      return;
    }
    
    try {
      await api.post('/marketplace/cart/add', {
        product_id: productId,
        quantity: 1
      });
      toast.success('Added to cart!');
      fetchCartCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-2 -ml-2"
              style={{ color: '#5C635A' }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
            <h1
              className="text-3xl sm:text-4xl font-bold tracking-tight"
              style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
            >
              Insight Warehouse
            </h1>
            <p className="mt-2" style={{ color: '#5C635A' }}>
              Shop products from our teachers and partners
            </p>
          </div>
          
          {isAuthenticated() && (
            <Button
              onClick={() => navigate('/marketplace/cart')}
              className="rounded-full relative"
              style={{ backgroundColor: '#163329' }}
              data-testid="cart-button"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Cart
              {cartCount > 0 && (
                <span 
                  className="absolute -top-2 -right-2 h-5 w-5 rounded-full flex items-center justify-center text-xs text-white"
                  style={{ backgroundColor: '#D86B47' }}
                >
                  {cartCount}
                </span>
              )}
            </Button>
          )}
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: '#5C635A' }} />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-full"
                style={{ borderColor: '#E3E1DB' }}
              />
            </div>
            <Button type="submit" className="rounded-full" style={{ backgroundColor: '#163329' }}>
              Search
            </Button>
          </form>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Button
            variant={selectedCategory === '' ? 'default' : 'outline'}
            onClick={() => handleCategoryChange('')}
            className="rounded-full"
            style={selectedCategory === '' ? { backgroundColor: '#163329' } : { borderColor: '#E3E1DB' }}
          >
            All Products
          </Button>
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.id] || Package;
            return (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                onClick={() => handleCategoryChange(cat.id)}
                className="rounded-full"
                style={selectedCategory === cat.id ? { backgroundColor: '#163329' } : { borderColor: '#E3E1DB' }}
              >
                <Icon className="h-4 w-4 mr-2" />
                {cat.name}
              </Button>
            );
          })}
        </div>

        {/* Products Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="h-48 bg-gray-200 rounded-t-lg"></div>
                <CardContent className="p-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <Package className="h-16 w-16 mx-auto mb-4" style={{ color: '#E3E1DB' }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: '#163329' }}>No products found</h3>
            <p style={{ color: '#5C635A' }}>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product, idx) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Link to={`/marketplace/product/${product.id}`}>
                  <Card 
                    className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer h-full"
                    style={{ borderColor: '#E3E1DB' }}
                    data-testid={`product-card-${product.id}`}
                  >
                    <div className="relative h-48 bg-gray-100">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={product.images[0]}
                          alt={product.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: '#F5F5F0' }}>
                          <Package className="h-12 w-12" style={{ color: '#E3E1DB' }} />
                        </div>
                      )}
                      {product.is_digital && (
                        <Badge className="absolute top-2 right-2" style={{ backgroundColor: '#163329' }}>
                          <Download className="h-3 w-3 mr-1" />
                          Digital
                        </Badge>
                      )}
                      {product.stock === 0 && !product.is_digital && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <span className="text-white font-bold text-lg">Out of Stock</span>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-semibold line-clamp-2" style={{ color: '#163329' }}>
                          {product.title}
                        </h3>
                      </div>
                      <p className="text-sm line-clamp-2 mb-3" style={{ color: '#5C635A' }}>
                        {product.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold" style={{ color: '#163329' }}>
                          <Usd value={product.price} />
                          <PriceCaption className="font-normal" />
                        </span>
                        <Button
                          size="sm"
                          className="rounded-full"
                          style={{ backgroundColor: '#163329' }}
                          onClick={(e) => addToCart(product.id, e)}
                          disabled={product.stock === 0 && !product.is_digital}
                          data-testid={`add-to-cart-${product.id}`}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs mt-2" style={{ color: '#5C635A' }}>
                        By {product.seller_name}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketplacePage;
